import { useState, useCallback, useEffect, useRef } from 'react';
import { engineService } from '../services/engineService';

/**
 * useEngine - ARES 엔진 세션 라이프사이클 및 상태 관리 훅
 *
 * 마운트 시 세션 초기화, 언마운트 시 세션 정리.
 * socket 이벤트 콜백(handleCandidatesUpdate, handleStateChange)을
 * GroupChat에 전달하여 실시간 후보 갱신.
 */
export const useEngine = (roomId) => {
  const [candidates, setCandidates] = useState({ time: [], place: [], person: [] });
  const [engineState, setEngineState] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [lastActorId, setLastActorId] = useState(null); // Firebase UID
  const [dgStrategy, setDgStrategy] = useState('stable');
  const [dgAutoDecision, setDgAutoDecision] = useState(null);
  // 참여자별 응답 상태: { [firebaseUid]: 'accepted' | 'rejected' | 'pending' }
  const [memberStatuses, setMemberStatuses] = useState({});
  const [isAutoConfirmed, setIsAutoConfirmed] = useState(false);
  const roomIdRef = useRef(roomId);

  useEffect(() => {
    roomIdRef.current = roomId;
  }, [roomId]);

  // 세션 초기화
  const initSession = useCallback(async () => {
    if (!roomId) return;
    setIsLoading(true);
    try {
      const result = await engineService.initRoomSession(roomId);
      if (result.success) {
        if (result.candidates) {
          const normalize = (pool) => {
            if (!pool) return [];
            return Array.isArray(pool) ? pool : [pool];
          };
          const c = result.candidates;
          setCandidates({
            time: normalize(c.time),
            place: normalize(c.place),
            person: normalize(c.person),
          });
        }
        if (result.state) setEngineState(result.state);
        setIsInitialized(true);
      }
    } catch (err) {
      // 세션이 이미 있거나 네트워크 오류 - 첫 action에서 서버가 자동 생성하므로 무시
      console.error('[useEngine] initSession failed:', err.message, err);
      // init 실패해도 isInitialized=true로 설정 → action 이벤트로 자동 복구 가능
      setIsInitialized(true);
    } finally {
      setIsLoading(false);
    }
  }, [roomId]);

  // socket 콜백: engine:candidates-updated
  const handleCandidatesUpdate = useCallback((data) => {
    if (!data) return;
    if (data.candidates) {
      const normalize = (pool) => {
        if (!pool) return [];
        return Array.isArray(pool) ? pool : [pool];
      };
      setCandidates({
        time: normalize(data.candidates.time),
        place: normalize(data.candidates.place),
        person: normalize(data.candidates.person),
      });
    }
    if (data.state) setEngineState(data.state);
    if (data.action) setLastAction(data.action);
    if (data.actorId !== undefined) setLastActorId(data.actorId);
    if (data.dgStrategy) setDgStrategy(data.dgStrategy);
    if (data.dgAutoDecision) setDgAutoDecision(data.dgAutoDecision);
    setIsAutoConfirmed(!!data.autoConfirmed);
    if (!isInitialized) setIsInitialized(true);

    // 액션 타입에 따라 actorId의 참여 상태 업데이트
    if (data.actorId && data.action?.type) {
      const type = data.action.type;
      if (type === 'CANCEL' || type === 'ABSTAIN') {
        // ABSTAIN: 불참 선언, CANCEL: 일정 취소
        setMemberStatuses(prev => ({ ...prev, [data.actorId]: 'rejected' }));
      } else if (type === 'CONFIRM' || type === 'PASSIVE_ACCEPT') {
        setMemberStatuses(prev => ({ ...prev, [data.actorId]: 'accepted' }));
      }
    }
  }, [isInitialized]);

  // socket 콜백: engine:state-changed
  const handleStateChange = useCallback((data) => {
    if (data?.vector) setEngineState(data.vector);
  }, []);

  // 세션 종료 (best-effort)
  const destroySession = useCallback(async () => {
    if (!roomIdRef.current) return;
    try {
      await engineService.destroySession(roomIdRef.current);
    } catch {
      // 세션이 이미 없거나 네트워크 오류 - 무시
    }
  }, []);

  // 라이프사이클: 마운트 시 초기화, 언마운트 시 정리
  useEffect(() => {
    if (roomId) {
      initSession();
    }
    return () => {
      if (roomIdRef.current) {
        destroySession();
      }
    };
  }, [roomId, initSession, destroySession]);

  return {
    candidates,
    engineState,
    isInitialized,
    isLoading,
    lastAction,
    lastActorId,
    dgStrategy,
    dgAutoDecision,
    memberStatuses,
    isAutoConfirmed,
    handleCandidatesUpdate,
    handleStateChange,
  };
};
